import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './contactfilter.css';

// Multi-select contact filter for the job list.
// An empty selection means "All contacts".
const ContactFilter = ({ contactChoices = [], selectedContacts, onChange, defaultContact }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const options = [...contactChoices]
    .sort((a, b) => a.order - b.order)
    .map(choice => ({ value: choice.value, label: choice.display_name }));
  // Keep selected values that are no longer in the choice list so they can still be unchecked
  selectedContacts.forEach(value => {
    if (!options.some(option => option.value === value)) {
      options.push({ value, label: value });
    }
  });

  const getLabel = (value) => options.find(option => option.value === value)?.label || value;

  let triggerText = 'All contacts';
  if (selectedContacts.length === 1) {
    triggerText = getLabel(selectedContacts[0]);
  } else if (selectedContacts.length > 1) {
    triggerText = `${getLabel(selectedContacts[0])} +${selectedContacts.length - 1}`;
  }

  // The panel is rendered into document.body with fixed positioning. iOS Safari clips
  // fixed elements inside the horizontally scrolling filter row, so it can't live there.
  const updatePanelPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const panelWidth = 240;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - panelWidth - 8));
    setPanelPosition({ top: rect.bottom + 4, left });
  };

  const toggleOpen = () => {
    if (!isOpen) updatePanelPosition();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      if (panelRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };
    const handleScroll = (event) => {
      // Keep the panel attached to the trigger when the filter row or page scrolls
      if (panelRef.current?.contains(event.target)) return;
      updatePanelPosition();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('scroll', handleScroll, true);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePanelPosition);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePanelPosition);
    };
  }, [isOpen]);

  const toggleContact = (value) => {
    if (selectedContacts.includes(value)) {
      onChange(selectedContacts.filter(contact => contact !== value));
    } else {
      onChange([...selectedContacts, value]);
    }
  };

  const selectOnly = (values) => {
    onChange(values);
    setIsOpen(false);
  };

  const isOnlyDefault = defaultContact && selectedContacts.length === 1 && selectedContacts[0] === defaultContact;

  return (
    <div className='contactfilter'>
      <button
        type='button'
        ref={triggerRef}
        className={`contactfiltertrigger ${selectedContacts.length > 0 ? 'active' : ''}`}
        onClick={toggleOpen}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        title={selectedContacts.length > 0 ? selectedContacts.map(getLabel).join(', ') : 'All contacts'}
      >
        <span className='contactfiltertriggertext'>{triggerText}</span>
        <span className='contactfiltercaret'>▾</span>
      </button>

      {isOpen && createPortal(
        <div
          ref={panelRef}
          className='contactfilterpanel'
          style={{ top: panelPosition.top, left: panelPosition.left }}
          role='listbox'
          aria-multiselectable='true'
        >
          <div className='contactfilterquick'>
            <button
              type='button'
              className={`contactfilterquickbutton ${selectedContacts.length === 0 ? 'selected' : ''}`}
              onClick={() => selectOnly([])}
            >
              All contacts
            </button>
            {defaultContact && (
              <button
                type='button'
                className={`contactfilterquickbutton ${isOnlyDefault ? 'selected' : ''}`}
                onClick={() => selectOnly([defaultContact])}
              >
                My default ({getLabel(defaultContact)})
              </button>
            )}
          </div>
          <div className='contactfilteroptions'>
            {options.map(option => (
              <label key={option.value} className='contactfilteroption'>
                <input
                  type='checkbox'
                  checked={selectedContacts.includes(option.value)}
                  onChange={() => toggleContact(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
            {options.length === 0 && (
              <div className='contactfilterempty'>No contacts set up</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ContactFilter;
